// @ts-check

/** @file Catalog/session lifecycle around retained per-project replicas. */

import Configuration from "velocious/build/src/configuration.js"

import Option from "../models/option.js"
import Project from "../models/project.js"
import User from "../models/user.js"
import {flushLocalDatabaseWrites} from "../shared/local-database-persistence.js"
import {projectCatalogCursorKey} from "../sync/project-catalog-key.js"
import {signOutOfflineSync} from "../sync/offline-sync-provider.js"
import {ProjectReplicaUnsyncedError, sealProjectReplicasForUser} from "./project-replica.js"

const OWNER_KEY = "replica-owner-user-id"
const PROFILE_KEY = "replica-current-user-profile"
const STATE_KEY = "replica-lifecycle-state"
const GENERATION_KEY = "replica-bootstrap-generation"
const LIFECYCLE_KEYS = [OWNER_KEY, PROFILE_KEY, STATE_KEY, GENERATION_KEY]

/** Compatibility name retained for auth callers and existing error handling. */
export class UnsyncedReplicaMutationsError extends ProjectReplicaUnsyncedError {}

/** @typedef {import("../auth/use-current-user.jsx").CurrentUserData} CurrentUserData */
/** @typedef {import("velocious/build/src/database/query/model-class-query.js").default<typeof Option>} OptionScope */
/** @typedef {{generation: string | null, ownerUserId: string | null, profile: CurrentUserData | null, state: "empty" | "bootstrapping" | "ready" | "sealed"}} ReplicaLifecycleSnapshot */

/**
 * Reads the catalog/session lifecycle without opening any project database.
 * @returns {Promise<ReplicaLifecycleSnapshot>} Durable lifecycle snapshot.
 */
export async function replicaLifecycleSnapshot() {
  const values = await optionValues()
  const state = values[STATE_KEY]
  return {
    generation: values[GENERATION_KEY] || null,
    ownerUserId: values[OWNER_KEY] || null,
    profile: parseProfile(values[PROFILE_KEY]),
    state: state === "bootstrapping" || state === "ready" || state === "sealed" ? state : "empty"
  }
}

/**
 * Returns a cached profile only for a completed matching catalog session.
 * @param {string | null | undefined} ownerUserId Expected session owner.
 * @returns {Promise<CurrentUserData | null>} Safe cached profile or null.
 */
export async function offlineCurrentUser(ownerUserId) {
  if (!ownerUserId) return null
  const snapshot = await replicaLifecycleSnapshot()
  if (snapshot.state !== "ready" || snapshot.ownerUserId !== String(ownerUserId)) return null
  if (!snapshot.profile || String(snapshot.profile.id) !== snapshot.ownerUserId) return null
  return snapshot.profile
}

/**
 * Stages a catalog bootstrap after safely sealing a different user's replicas.
 * @param {CurrentUserData} profile Authenticated user profile.
 * @param {{discardPending?: boolean}} [options] Guarded transition options.
 * @returns {Promise<string>} New durable bootstrap generation.
 * @rejects {Error} When unsynced work or an explicit discard would be unsafe.
 */
export async function beginReplicaBootstrap(profile, {discardPending = false} = {}) {
  const ownerUserId = String(profile.id)
  const snapshot = await replicaLifecycleSnapshot()
  if (snapshot.ownerUserId && snapshot.ownerUserId !== ownerUserId) {
    if (discardPending) {
      throw new Error("Per-project replica mutations cannot be discarded by catalog bootstrap")
    }
    await assertReplicaMayBeCleared()
    await clearCatalogSession()
  }

  const generation = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  await Configuration.current().withTransaction({databaseIdentifier: "default", name: "Begin replica catalog bootstrap"}, async (operation) => {
    const options = operation.forModel(Option)
    await writeOption(options, OWNER_KEY, ownerUserId)
    await writeOption(options, PROFILE_KEY, JSON.stringify(profile))
    await writeOption(options, GENERATION_KEY, generation)
    await writeOption(options, STATE_KEY, "bootstrapping")
  })
  await flushLocalDatabaseWrites()
  return generation
}

/**
 * Atomically publishes a fully staged catalog bootstrap for offline use.
 * @param {{generation: string, ownerUserId: string}} args Expected durable identity.
 * @returns {Promise<void>} Completion after publishing the ready state.
 * @rejects {Error} When durable bootstrap identity does not match.
 */
export async function completeReplicaBootstrap({generation, ownerUserId}) {
  await Configuration.current().withTransaction({databaseIdentifier: "default", name: "Complete replica catalog bootstrap"}, async (operation) => {
    const options = operation.forModel(Option)
    const [storedGeneration, storedOwner, storedProfile, storedState] = await Promise.all([
      options.findBy({key: GENERATION_KEY}), options.findBy({key: OWNER_KEY}),
      options.findBy({key: PROFILE_KEY}), options.findBy({key: STATE_KEY})
    ])
    const profile = parseProfile(storedProfile?.value())
    if (storedState?.value() !== "bootstrapping" || storedGeneration?.value() !== generation ||
      storedOwner?.value() !== String(ownerUserId) || String(profile?.id) !== String(ownerUserId)) {
      throw new Error("Replica bootstrap completion does not match its durable owner and generation")
    }
    storedState.setValue("ready")
    await storedState.save()
  })
  await flushLocalDatabaseWrites()
}

/**
 * Seals every clean project handle and then makes the cached profile unavailable.
 * @param {{migrations?: import("velocious/build/src/database/migrator/types.js").RequireMigrationContextType}} [options] Optional migration context.
 * @returns {Promise<void>} Completion after sealing catalog and replicas.
 * @rejects {UnsyncedReplicaMutationsError | Error} When local intent remains or sealing fails.
 */
export async function sealReplica({migrations} = {}) {
  const snapshot = await replicaLifecycleSnapshot()
  if (!snapshot.ownerUserId) return
  try {
    await sealProjectReplicasForUser({migrations, userId: snapshot.ownerUserId})
  } catch (error) {
    if (error instanceof ProjectReplicaUnsyncedError) throw new UnsyncedReplicaMutationsError(error.unsyncedCount)
    throw error
  }
  await writeLifecycleState("sealed")
}

/**
 * Fails before an auth transition if a project queue has pending/failed intent.
 * @returns {Promise<void>} Completion when every retained project is clean.
 * @rejects {UnsyncedReplicaMutationsError | Error} When local intent remains or inspection fails.
 */
export async function assertReplicaMayBeCleared() {
  const snapshot = await replicaLifecycleSnapshot()
  if (!snapshot.ownerUserId) return
  try {
    await sealProjectReplicasForUser({restoreOnFailure: true, userId: snapshot.ownerUserId})
  } catch (error) {
    if (error instanceof ProjectReplicaUnsyncedError) throw new UnsyncedReplicaMutationsError(error.unsyncedCount)
    throw error
  }
}

/**
 * Clears the lightweight catalog/session only. Per-project databases and the
 * old experimental single-replica tables are retained and never used as a
 * fallback; no sign-out path silently destroys them.
 * @param {{discardPending?: boolean}} [options] Guarded clearing options.
 * @returns {Promise<void>} Completion after catalog/session clearing.
 * @rejects {Error} When discard is requested or a replica is unsafe.
 */
export async function clearReplica({discardPending = false} = {}) {
  if (discardPending) throw new Error("Per-project replica storage cannot be discarded by clearReplica")
  await assertReplicaMayBeCleared()
  await clearCatalogSession()
}

/**
 * Clears current catalog rows and lifecycle Options, preserving the registry.
 * @returns {Promise<void>} Completion after the catalog flush.
 */
async function clearCatalogSession() {
  const {ownerUserId} = await replicaLifecycleSnapshot()

  // Disarm the app-level offline queue and drop the durable grant before the
  // catalog/session is cleared; a later sign-in re-acquires on replica open.
  await signOutOfflineSync()

  await Configuration.current().withTransaction({databaseIdentifier: "default", name: "Clear replica catalog session"}, async (operation) => {
    await operation.forModel(Project).destroyAll()
    await operation.forModel(User).destroyAll()
    await operation.forModel(Option).where({key: LIFECYCLE_KEYS}).destroyAll()
    if (ownerUserId) await operation.forModel(Option).where({key: projectCatalogCursorKey(ownerUserId)}).destroyAll()
  })
  await flushLocalDatabaseWrites()
}

/**
 * Persists one lifecycle state.
 * @param {string} state New catalog state.
 * @returns {Promise<void>} Completion after the default database flush.
 */
async function writeLifecycleState(state) {
  await Configuration.current().withTransaction({databaseIdentifier: "default", name: "Seal replica catalog session"}, async (operation) => {
    await writeOption(operation.forModel(Option), STATE_KEY, state)
  })
  await flushLocalDatabaseWrites()
}

/**
 * Loads lifecycle values from the installation Option store.
 * @returns {Promise<Record<string, string>>} Values keyed by lifecycle option name.
 */
async function optionValues() {
  const rows = await Option.where({key: LIFECYCLE_KEYS}).toArray()
  const values = /** @type {Record<string, string>} */ ({})
  for (const row of rows) values[row.key()] = row.value()
  return values
}

/**
 * Upserts one lifecycle option through the current transaction scope.
 * @param {OptionScope} scope Transaction-bound Option query.
 * @param {string} key Installation-store lookup name.
 * @param {string} value Serialized lifecycle payload.
 * @returns {Promise<void>} Completion after the option is saved.
 */
async function writeOption(scope, key, value) {
  const row = await scope.findOrInitializeBy({key})
  row.setValue(value)
  await row.save()
}

/**
 * Parses a cached profile without trusting malformed storage.
 * @param {string | null | undefined} value Serialized profile.
 * @returns {CurrentUserData | null} Parsed profile or null.
 */
function parseProfile(value) {
  if (!value) return null
  try {
    const profile = JSON.parse(value)
    return profile && typeof profile === "object" && profile.id ? profile : null
  } catch {
    return null
  }
}
